/* eslint-disable react-hooks/exhaustive-deps */
import { useAdminUser, useAlert, useCompany } from '@orientaction/hooks';
import { adminService, BookTemplate, servicesService } from '@orientaction/services';
import { getFromLS } from '@orientaction/utils';
import { validate } from 'email-validator';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { apiServiceNative } from '../../../utils/fetchNative';
import { fetchAssignableStaffUsers } from '../shared/assignableStaff';
import {
  BENEFICIARY_ROLE_ID,
  A_LA_CARTE_SERVICE_ID,
  BILAN_COMPETENCES_SERVICE_ID,
  CONSULTANT_ROLE_ID,
  EMAIL_REGEX,
  OUTPLACEMENT_SERVICE_ID,
} from '../shared/constants';
import { buildSingleServiceResourcesMap } from '../shared/serviceResourcesMap';
import {
  filterMinorCategoriesForStaff,
  getStaffRoleLabel,
  hasRequiredBeneficiaryInfos,
  normalizeServiceList,
  readRole,
  resourceMatchesService,
  sortAssignableStaff,
  toDayOfBirth,
  toId,
  toResourceFromTemplate,
  toServiceOption,
  unwrapCollection,
  unwrapRelation,
} from '../shared/utils';
import { CREATE_SUCCESS, initialInfos } from './constants';
import {
  ConsultantOption,
  ExistingUser,
  InfosForm,
  ResourceItem,
  ResourceKind,
  ServiceOption,
  WizardStep,
} from './types';

const qs = require('qs');

const mapExistingUser = (user: any): ExistingUser => ({
  id: user.id,
  email: user.email,
  firstname: user.firstname,
  lastname: user.lastname,
  photoUri: user.photoUri,
  rattachement: user.rattachement,
  service: toServiceOption(user.service),
  services: normalizeServiceList(user.services),
  company_user_role: user.company_user_role,
  upper_hierarchy: user.upper_hierarchy,
});

const toCheckedPayload = (catalog: ResourceItem[], selected: ResourceItem[]) => {
  const selectedIds = new Set(selected.map(item => item.id));
  return catalog.map(item => ({
    id: item.id,
    isChecked: selectedIds.has(item.id),
    query: item.query,
    attributes: {
      name: item.label,
      query: item.query,
    },
  }));
};

export const useCreateBeneficiaryWizard = () => {
  const history = useHistory();
  const alert = useAlert();
  const {
    company,
    updateArrayAfterUpdateCreate,
    setCountTabs,
    countTabs,
    setTabs,
  } = useAdminUser();
  const { companies } = useCompany();

  const token = getFromLS('userToken') || '';
  const userConnected = JSON.parse(getFromLS('userInfos') || '{}');
  const connectedUserId = userConnected?.id;
  const isConsultant = userConnected?.company_user_role?.id === CONSULTANT_ROLE_ID;
  const companyId = company || userConnected?.company?.id || 1;
  const companyAlias = (companies || []).find((item: any) => Number(item.id) === Number(companyId))
    ?.attributes?.companyUserRoleAlias;

  const [step, setStep] = useState<WizardStep>(1);
  const [email, setEmail] = useState('');
  const [existingUser, setExistingUser] = useState<ExistingUser | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [infos, setInfos] = useState<InfosForm>(initialInfos);
  const [consultantId, setConsultantId] = useState<number | ''>(
    isConsultant ? Number(userConnected.id) : ''
  );
  const [serviceId, setServiceId] = useState<number | ''>('');
  const [consultants, setConsultants] = useState<ConsultantOption[]>([]);
  const [availableServices, setAvailableServices] = useState<ServiceOption[]>([]);
  const [majorCatalog, setMajorCatalog] = useState<ResourceItem[]>([]);
  const [minorCatalog, setMinorCatalog] = useState<ResourceItem[]>([]);
  const [gameCatalog, setGameCatalog] = useState<ResourceItem[]>([]);
  const [ebookCatalog, setEbookCatalog] = useState<ResourceItem[]>([]);
  const [bookCatalog, setBookCatalog] = useState<ResourceItem[]>([]);
  const [majorTests, setMajorTests] = useState<ResourceItem[]>([]);
  const [minorTests, setMinorTests] = useState<ResourceItem[]>([]);
  const [books, setBooks] = useState<ResourceItem[]>([]);
  const [games, setGames] = useState<ResourceItem[]>([]);
  const [ebooks, setEbooks] = useState<ResourceItem[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isEmailValid = validate(email.trim()) && EMAIL_REGEX.test(email.trim());
  const canGoInfos = hasRequiredBeneficiaryInfos(infos);
  const canGoResources = Boolean(consultantId) && Boolean(serviceId);

  const selectedConsultant = useMemo(
    () => consultants.find(item => Number(item.id) === Number(consultantId)),
    [consultants, consultantId]
  );
  const isPrestationStep = step >= 3;
  const consultantRoleId = Number(selectedConsultant?.company_user_role?.id) || 0;
  const consultantCategoryKey = (selectedConsultant?.category_tests || [])
    .map((category: any) => Number(category.id))
    .filter((id: number) => id > 0)
    .sort((a: number, b: number) => a - b)
    .join(',');

  const existingPrestations = useMemo(() => {
    if (!existingUser) {
      return [];
    }
    const byId = new Map<number, ServiceOption>();
    [...(existingUser.services || []), existingUser.service].filter(Boolean).forEach(service => {
      const option = toServiceOption(service);
      if (option) {
        byId.set(option.id, option);
      }
    });
    return Array.from(byId.values());
  }, [existingUser]);

  const goBack = () => {
    history.push('/admin/users-resultats');
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (existingUser) {
      setExistingUser(null);
    }
  };

  const fetchExistingUser = async (value: string) => {
    const params = qs.stringify(
      {
        filters: { email: { $eq: value.trim().toLowerCase() } },
        populate: ['service', 'services', 'company_user_role', 'upper_hierarchy'],
      },
      { encodeValuesOnly: true }
    );
    const response = await apiServiceNative.get(`/api/users?${params}`);
    const list = unwrapCollection(response);
    return list[0] ? mapExistingUser(list[0]) : null;
  };

  const goNextFromEmail = async () => {
    if (!isEmailValid || existingUser) {
      return;
    }
    setCheckingEmail(true);
    try {
      const found = await fetchExistingUser(email);
      if (found) {
        setExistingUser(found);
        return;
      }
      setExistingUser(null);
      setStep(2);
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setCheckingEmail(false);
    }
  };

  const openExistingUserProfile = () => {
    if (!existingUser) {
      return;
    }
    const roleId = readRole(existingUser.company_user_role)?.id;
    if (roleId === BENEFICIARY_ROLE_ID) {
      history.push(`/admin/users-resultats/beneficiaire/${existingUser.id}?tab=prestations`);
      return;
    }
    history.push(`/admin/users-resultats/consultant/${existingUser.id}`);
  };

  const updateInfos = (patch: Partial<InfosForm>) => {
    setInfos(prev => ({ ...prev, ...patch }));
  };

  useEffect(() => {
    if (!isPrestationStep) {
      return undefined;
    }

    let cancelled = false;

    const mapStaffOption = (user: any): ConsultantOption => {
      const role = readRole(user.company_user_role) || undefined;
      return {
        id: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        company_user_role: role,
        roleLabel: getStaffRoleLabel(role, companyAlias),
        allowed_services: normalizeServiceList(user.allowed_services),
        category_tests: user.category_tests || [],
      };
    };

    const loadConsultants = async () => {
      try {
        const users = await fetchAssignableStaffUsers({
          companyId,
          consultantOnly: isConsultant,
        });
        if (cancelled) {
          return;
        }
        let list: ConsultantOption[] = users.map(mapStaffOption);

        if (
          isConsultant &&
          connectedUserId &&
          !list.some(item => Number(item.id) === Number(connectedUserId))
        ) {
          list = [mapStaffOption(userConnected), ...list];
        }
        setConsultants(sortAssignableStaff(list));
      } catch (error) {
        if (!cancelled) {
          alert.openSimpleAlert();
        }
      }
    };

    loadConsultants();
    return () => {
      cancelled = true;
    };
  }, [isPrestationStep, companyId, connectedUserId, isConsultant, companyAlias]);

  useEffect(() => {
    if (!isPrestationStep || !consultantId) {
      return undefined;
    }

    let cancelled = false;

    const loadServicesForConsultant = async () => {
      const consultant = consultants.find(item => Number(item.id) === Number(consultantId));
      let fromAllowed = consultant?.allowed_services || [];
      if (!fromAllowed.length) {
        try {
          const response = await servicesService.getUserServices(token, consultantId);
          fromAllowed = normalizeServiceList(response?.data?.data || response?.data);
        } catch (error) {
          fromAllowed = [];
        }
      }

      if (cancelled) {
        return;
      }

      const withInCard = [...fromAllowed];
      if (!withInCard.some(item => item.id === A_LA_CARTE_SERVICE_ID)) {
        withInCard.push({ id: A_LA_CARTE_SERVICE_ID, name: 'A la carte' });
      }
      setAvailableServices(withInCard);
    };

    loadServicesForConsultant();
    return () => {
      cancelled = true;
    };
  }, [isPrestationStep, consultantId, consultants, token]);

  useEffect(() => {
    setServiceId('');
    setMajorTests([]);
    setMinorTests([]);
    setBooks([]);
    setGames([]);
    setEbooks([]);
  }, [consultantId]);

  useEffect(() => {
    setMajorTests([]);
    setMinorTests([]);
    setBooks([]);
    setGames([]);
    setEbooks([]);
  }, [serviceId]);

  useEffect(() => {
    if (step !== 4 || !serviceId || !consultantId) {
      return undefined;
    }

    let cancelled = false;

    const loadResourceCatalogs = async () => {
      setLoadingCatalogs(true);
      try {
        const populate = {
          major_tests_templates: {
            fields: ['id', 'name', 'query'],
            sort: ['id:asc'],
          },
          book_templates: {
            fields: ['id', 'name', 'query', 'route', 'HourRead'],
            sort: ['id:asc'],
          },
        };
        const skipMinors = Number(serviceId) === OUTPLACEMENT_SERVICE_ID;
        let categoriesPromise: Promise<any> = Promise.resolve({ data: [] });
        if (!skipMinors) {
          const stored = sessionStorage.getItem('categories-test');
          if (stored) {
            try {
              categoriesPromise = Promise.resolve(JSON.parse(stored));
            } catch {
              categoriesPromise = apiServiceNative.get('/api/category-tests?populate=tests');
            }
          } else {
            categoriesPromise = apiServiceNative.get('/api/category-tests?populate=tests');
          }
        }
        const [serviceRes, gamesRes, ebooksRes, categoriesRes] = await Promise.all([
          servicesService.getServiceById(token, serviceId, ['id', 'name'], {}, populate),
          apiServiceNative.get('/api/game-list'),
          apiServiceNative.get('/api/ebook-list'),
          categoriesPromise,
        ]);

        if (cancelled) {
          return;
        }

        const servicePayload = serviceRes?.data?.data || serviceRes?.data || serviceRes;
        const serviceAttrs = servicePayload?.attributes || servicePayload;
        setMajorCatalog(
          unwrapRelation(serviceAttrs?.major_tests_templates).map((item: any) =>
            toResourceFromTemplate(item)
          )
        );

        let bookRaw = unwrapRelation(serviceAttrs?.book_templates);
        if (!bookRaw.length && Number(serviceId) === BILAN_COMPETENCES_SERVICE_ID) {
          const booksRes = await BookTemplate.getUserBooks(token, { userId: 0 });
          bookRaw = booksRes?.data?.books || [];
        }
        if (cancelled) {
          return;
        }
        setBookCatalog(bookRaw.map((item: any) => toResourceFromTemplate(item)));

        const gameList = unwrapCollection(gamesRes?.data || gamesRes);
        setGameCatalog(
          gameList
            .filter((game: any) => game.status !== 'draft')
            .filter((game: any) => resourceMatchesService(game, Number(serviceId)))
            .map((game: any) => ({
              id: Number(game.id),
              label: game.title || game.name || `Serious game ${game.id}`,
            }))
        );

        const ebookList = unwrapCollection(ebooksRes?.data || ebooksRes);
        setEbookCatalog(
          ebookList
            .filter((ebook: any) => ebook.status !== 'draft')
            .filter((ebook: any) => resourceMatchesService(ebook, Number(serviceId)))
            .map((ebook: any) => ({
              id: Number(ebook.id),
              label: ebook.title || ebook.name || `Ebook ${ebook.id}`,
            }))
        );

        const categories = filterMinorCategoriesForStaff(
          unwrapCollection(categoriesRes?.data || categoriesRes),
          {
            company_user_role: selectedConsultant?.company_user_role,
            categoryIds: (selectedConsultant?.category_tests || []).map((category: any) =>
              Number(category.id)
            ),
          }
        ).sort(
          (a: any, b: any) =>
            Number(a.attributes?.priority || a.priority || 0) -
            Number(b.attributes?.priority || b.priority || 0)
        );

        const minors: ResourceItem[] = [];
        categories.forEach((category: any) => {
          const group = category.attributes?.name || category.name || '';
          const groupSubtitle = category.attributes?.subtitle || category.subtitle || '';
          const groupId = toId(category);
          const tests = unwrapRelation(category.attributes?.tests || category.tests)
            .filter((test: any) => {
              const attrs = test.attributes || test;
              return !attrs.isArchived && !attrs.isDesabled;
            })
            .sort(
              (a: any, b: any) =>
                Number(a.attributes?.priority || a.priority || 0) -
                Number(b.attributes?.priority || b.priority || 0)
            );
          tests.forEach((test: any) => {
            minors.push(toResourceFromTemplate(test, group, groupSubtitle, groupId));
          });
        });
        setMinorCatalog(minors);
      } catch (error) {
        if (!cancelled) {
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setLoadingCatalogs(false);
        }
      }
    };

    loadResourceCatalogs();
    return () => {
      cancelled = true;
    };
  }, [step, serviceId, consultantId, consultantRoleId, consultantCategoryKey, token]);

  const toggleResource = (kind: ResourceKind, item: ResourceItem, checked: boolean) => {
    const setters = {
      majorTests: setMajorTests,
      minorTests: setMinorTests,
      books: setBooks,
      games: setGames,
      ebooks: setEbooks,
    };
    const current = {
      majorTests,
      minorTests,
      books,
      games,
      ebooks,
    }[kind];
    if (checked) {
      if (current.some(entry => entry.id === item.id)) {
        return;
      }
      setters[kind]([...current, { ...item, status: item.status || 'todo' }]);
      return;
    }
    setters[kind](current.filter(entry => entry.id !== item.id));
  };

  const removeResource = (kind: ResourceKind, id: number) => {
    const setters = {
      majorTests: setMajorTests,
      minorTests: setMinorTests,
      books: setBooks,
      games: setGames,
      ebooks: setEbooks,
    };
    const current = {
      majorTests,
      minorTests,
      books,
      games,
      ebooks,
    }[kind];
    setters[kind](current.filter(entry => entry.id !== id));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const selectedService = availableServices.find(item => Number(item.id) === Number(serviceId));
      const serviceResourcesMap = selectedService
        ? buildSingleServiceResourcesMap(selectedService.id, {
            upperHierarchy: consultantId,
            minorTests,
            books,
            majorTests,
            games,
            ebooks,
          })
        : undefined;
      const payload = {
        email: email.trim().toLowerCase(),
        username: email.trim().toLowerCase(),
        firstname: infos.firstname.trim(),
        lastname: infos.lastname.trim(),
        gender: infos.gender || null,
        diploma: infos.diploma || null,
        postal_code: infos.postal_code,
        town: infos.town,
        phone: infos.phone,
        language: infos.language === '' ? null : Number(infos.language),
        company: companyId,
        company_user_role: 5,
        upper_hierarchy: consultantId,
        rattachement: true,
        service: selectedService ? { id: selectedService.id, name: selectedService.name } : null,
        services: selectedService ? [{ id: selectedService.id, name: selectedService.name }] : [],
        books: toCheckedPayload(bookCatalog, books),
        tests: toCheckedPayload(majorCatalog, majorTests),
        tests_to_do: minorTests.map(test => ({ id: test.id })),
        ...(serviceResourcesMap ? { service_resources_map: serviceResourcesMap } : {}),
        Direction: userConnected.Direction || null,
      };

      const response = await adminService.register(payload, token);
      if (response?.data.data === 'E-mail already exist') {
        alert.setOpenAlert(
          true,
          'Cette adresse e-mail existe déjà, merci d’en utiliser une autre',
          'error'
        );
        setStep(1);
        return;
      }
      if (response?.data?.code === 'transform-beneficiary') {
        alert.setOpenAlert(
          true,
          'Cette adresse mail correspond déjà à un utilisateur non rattaché.',
          'error'
        );
        setStep(1);
        const found = await fetchExistingUser(email);
        if (found) {
          setExistingUser(found);
        }
        return;
      }

      const created = response?.data?.data;
      if (created?.id) {
        await apiServiceNative.put(`/api/users/${created.id}`, {
          authorized_games: games.map(game => game.id),
          authorized_ebooks: ebooks.map(ebook => ebook.id),
          dayOfBirth: toDayOfBirth(infos.dayOfBirth),
        });
      }

      if (created) {
        updateArrayAfterUpdateCreate(created);
        setCountTabs({
          ...countTabs,
          5: (countTabs[5] || 0) + 1,
        });
        setTabs(5);
      }
      alert.setOpenAlert(true, CREATE_SUCCESS, 'success');
      history.push('/admin/users-resultats');
    } catch (error) {
      console.error(error, 'error');
      alert.openSimpleAlert();
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    if (step === 1) {
      await goNextFromEmail();
      return;
    }
    if (step === 2 && canGoInfos) {
      setStep(3);
      return;
    }
    if (step === 3 && canGoResources) {
      setStep(4);
    }
  };

  const goPrevious = () => {
    if (step === 1) {
      goBack();
      return;
    }
    setStep(prev => (prev - 1) as WizardStep);
  };

  return {
    step,
    email,
    existingUser,
    existingPrestations,
    checkingEmail,
    infos,
    consultantId,
    serviceId,
    consultants,
    availableServices,
    selectedConsultant,
    majorCatalog,
    minorCatalog,
    bookCatalog,
    gameCatalog,
    ebookCatalog,
    majorTests,
    minorTests,
    books,
    games,
    ebooks,
    loadingCatalogs,
    submitting,
    isEmailValid,
    canGoInfos,
    canGoResources,
    isConsultant,
    handleEmailChange,
    updateInfos,
    setConsultantId,
    setServiceId,
    toggleResource,
    removeResource,
    goNext,
    goPrevious,
    goBack,
    handleSubmit,
    openExistingUserProfile,
  };
};
